import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Button,
  Text,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Input,
  Spinner,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Field,
  Textarea,
  Select,
  Option,
  Checkbox,
  Divider,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  TabList,
  Tab,
  makeStyles,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Search24Regular,
  DocumentText24Regular,
  Edit24Regular,
  Delete24Regular,
  MoreHorizontal24Regular,
  People24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { surveysApi, questionsApi, usersApi, assignmentsApi, botContextApi, Question, Survey, ContextVersion } from '../services/api.ts';
import PageHeader from '../components/PageHeader.tsx';
import StatusBadge from '../components/StatusBadge.tsx';
import EmptyState from '../components/EmptyState.tsx';
import Pagination from '../components/Pagination.tsx';
import ConfirmDialog from '../components/ConfirmDialog.tsx';
import QuestionCard from '../components/QuestionCard.tsx';
import ContextEditor from '../components/ContextEditor.tsx';
import { useToast } from '../components/Toast.tsx';

const MOBILE = '@media (max-width: 768px)';

const useStyles = makeStyles({
  card: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-lg)',
  },
  table: {
    minWidth: '720px',
    // On mobile we drop the extra columns, so the table fits with no
    // horizontal scroll.
    [MOBILE]: {
      minWidth: 0,
    },
  },
  hideOnMobile: {
    [MOBILE]: {
      display: 'none',
    },
  },
  nameCell: {
    [MOBILE]: {
      cursor: 'pointer',
    },
  },
  expandHint: {
    display: 'none',
    [MOBILE]: {
      display: 'inline',
      marginLeft: 'var(--space-xs)',
      color: 'var(--color-text-tertiary)',
      fontSize: '12px',
    },
  },
  detailMeta: {
    display: 'flex',
    gap: 'var(--space-lg)',
    width: '100%',
    marginBottom: 'var(--space-xs)',
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-md)',
    flexWrap: 'wrap',
    marginBottom: 'var(--space-md)',
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    maxWidth: '300px',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    color: 'var(--color-text-tertiary)',
    pointerEvents: 'none',
  },
  counters: {
    display: 'flex',
    gap: 'var(--space-lg)',
    alignItems: 'center',
  },
  counter: { textAlign: 'center' },
  counterValue: { fontSize: '18px', fontWeight: '600', lineHeight: '1', display: 'block' },
  counterLabel: { fontSize: '11px', color: 'var(--color-text-secondary)', display: 'block', marginTop: '2px' },
  questionsArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-sm)',
    maxHeight: '400px',
    overflowY: 'auto',
    paddingRight: 'var(--space-xs)',
  },
  infoBlock: {
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'var(--color-background)',
    borderRadius: 'var(--radius-sm)',
  },
  infoRow: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.8',
  },
  checkboxList: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    padding: 'var(--space-sm)',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    padding: '4px 0',
  },
  tabContent: {
    paddingTop: 'var(--space-md)',
  },
  manageActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    gap: 'var(--space-sm)',
    [MOBILE]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  manageActionsGroup: {
    display: 'flex',
    gap: 'var(--space-sm)',
    [MOBILE]: {
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
  },
  // Mobile-only popup (blurred backdrop + centered panel). Hidden on desktop.
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-lg)',
    zIndex: 1000,
    animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
    animationDuration: '0.18s',
    // Safety: this popup is a mobile affordance only.
    '@media (min-width: 769px)': {
      display: 'none',
    },
  },
  modalPanel: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: 'var(--space-lg)',
    width: '100%',
    maxWidth: '420px',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.96)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: '0.2s',
    animationTimingFunction: 'ease',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'var(--space-sm)',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-sm)',
  },
});

function useIsMobile(): boolean {
  const query = '(max-width: 768px)';
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function Surveys() {
  const s = useStyles();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [mobileSurvey, setMobileSurvey] = useState<Survey | null>(null);
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSurveyTitle, setNewSurveyTitle] = useState('');
  const [newSurveyDescription, setNewSurveyDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Questions for new survey
  const [questions, setQuestions] = useState<Partial<Question>[]>([]);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Partial<Question> | null>(null);
  const [questionText, setQuestionText] = useState('');
  const [questionInstructions, setQuestionInstructions] = useState('');
  const [answerCriteria, setAnswerCriteria] = useState('');

  // Manage dialog
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [editSurveyTitle, setEditSurveyTitle] = useState('');
  const [editSurveyDescription, setEditSurveyDescription] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [manageTab, setManageTab] = useState<string>('details');

  // Question in survey dialog
  const [showQuestionDialogInSurvey, setShowQuestionDialogInSurvey] = useState(false);
  const [editingQuestionInSurvey, setEditingQuestionInSurvey] = useState<Partial<Question> | null>(null);
  const [questionTextInSurvey, setQuestionTextInSurvey] = useState('');
  const [questionInstructionsInSurvey, setQuestionInstructionsInSurvey] = useState('');
  const [answerCriteriaInSurvey, setAnswerCriteriaInSurvey] = useState('');
  const [isCreatingQuestionInSurvey, setIsCreatingQuestionInSurvey] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  // Assign dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedSurveyForAssignment, setSelectedSurveyForAssignment] = useState<Survey | null>(null);
  const [selectedUsersForAssignment, setSelectedUsersForAssignment] = useState<number[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);

  // Context editor
  const [showContextDialog, setShowContextDialog] = useState(false);
  const [selectedSurveyForContext, setSelectedSurveyForContext] = useState<Survey | null>(null);

  // Confirm
  const [confirmAction, setConfirmAction] = useState<{ type: string; data?: any } | null>(null);

  // Queries
  const { data: surveys, isLoading } = useQuery('surveys', () => surveysApi.list({ limit: 100 }));
  const { data: availableUsers, refetch: refetchAvailableUsers } = useQuery(
    'available-users', () => usersApi.getAvailableForAssignment({ limit: 100 }),
    { staleTime: 0, cacheTime: 0 }
  );
  const { data: manageSurveyQuestions, isLoading: isLoadingQuestions } = useQuery(
    ['survey-questions', editingSurvey?.id],
    () => questionsApi.list({ survey_id: editingSurvey?.id }),
    { enabled: !!editingSurvey?.id }
  );

  // Mutations
  const createSurveyMutation = useMutation(surveysApi.create, {
    onSuccess: async (survey) => {
      if (questions.length > 0) {
        try {
          for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            await questionsApi.create({ survey_id: survey.id, type: q.type!, text: q.text!, answer_criteria: q.answer_criteria, order: i + 1, is_required: q.is_required ?? true });
          }
        } catch { toast.warning('Survey created but some questions may have failed'); }
      }
      queryClient.invalidateQueries('surveys');
      setShowCreateDialog(false);
      resetCreateForm();
      toast.success('Survey created');
    },
    onError: () => toast.error('Failed to create survey'),
  });

  const updateSurveyMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<Survey> }) => surveysApi.update(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries('surveys'); setShowManageDialog(false); resetEditForm(); toast.success('Survey updated'); },
      onError: () => toast.error('Failed to update survey'),
    }
  );

  const deleteSurveyMutation = useMutation(surveysApi.delete, {
    onSuccess: () => { queryClient.invalidateQueries('surveys'); setShowManageDialog(false); resetEditForm(); toast.success('Survey deleted'); },
    onError: () => toast.error('Failed to delete survey'),
  });

  const archiveSurveyMutation = useMutation(surveysApi.archive, {
    onSuccess: () => { queryClient.invalidateQueries('surveys'); setShowManageDialog(false); resetEditForm(); toast.success('Survey archived'); },
    onError: () => toast.error('Failed to archive survey'),
  });

  const publishSurveyMutation = useMutation(surveysApi.publish, {
    onSuccess: () => { queryClient.invalidateQueries('surveys'); setShowManageDialog(false); resetEditForm(); toast.success('Survey published'); },
    onError: () => toast.error('Failed to publish survey'),
  });

  const reorderQuestionsMutation = useMutation(questionsApi.reorder, {
    onSuccess: () => { queryClient.invalidateQueries(['survey-questions', editingSurvey?.id]); queryClient.invalidateQueries('surveys'); },
    onError: () => toast.error('Failed to reorder questions'),
  });

  const createQuestionInSurveyMutation = useMutation(questionsApi.create, {
    onSuccess: () => { queryClient.invalidateQueries(['survey-questions', editingSurvey?.id]); queryClient.invalidateQueries('surveys'); setShowQuestionDialogInSurvey(false); resetQuestionFormInSurvey(); toast.success('Question added'); },
    onError: () => toast.error('Failed to create question'),
  });

  const updateQuestionInSurveyMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<Question> }) => questionsApi.update(id, data),
    {
      onSuccess: () => { queryClient.invalidateQueries(['survey-questions', editingSurvey?.id]); queryClient.invalidateQueries('surveys'); setShowQuestionDialogInSurvey(false); resetQuestionFormInSurvey(); toast.success('Question updated'); },
      onError: () => toast.error('Failed to update question'),
    }
  );

  const deleteQuestionInSurveyMutation = useMutation(questionsApi.delete, {
    onSuccess: () => { queryClient.invalidateQueries(['survey-questions', editingSurvey?.id]); queryClient.invalidateQueries('surveys'); toast.success('Question deleted'); },
    onError: () => toast.error('Failed to delete question'),
  });

  const createBulkAssignmentMutation = useMutation(assignmentsApi.createBulk, {
    onSuccess: () => { queryClient.invalidateQueries('assignments'); queryClient.invalidateQueries('available-users'); setShowAssignDialog(false); setSelectedSurveyForAssignment(null); setSelectedUsersForAssignment([]); toast.success('Users assigned'); },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to assign users'),
  });

  // Form helpers
  const resetCreateForm = () => { setNewSurveyTitle(''); setNewSurveyDescription(''); setQuestions([]); setQuestionText(''); setQuestionInstructions(''); setAnswerCriteria(''); setEditingQuestion(null); };
  const resetEditForm = () => { setEditingSurvey(null); setEditSurveyTitle(''); setEditSurveyDescription(''); setManageTab('details'); };
  const resetQuestionFormInSurvey = () => { setEditingQuestionInSurvey(null); setQuestionTextInSurvey(''); setQuestionInstructionsInSurvey(''); setAnswerCriteriaInSurvey(''); };

  const parseCriteriaAndInstructions = (combinedString: string | null) => {
    if (!combinedString) return { parsedCriteria: null, parsedInstructions: null };
    const prefix = '",\n        "instructions": "';
    const idx = combinedString.indexOf(prefix);
    if (idx !== -1) {
      let c = combinedString.substring(0, idx); let inst = combinedString.substring(idx + prefix.length);
      if (c.endsWith('"')) c = c.slice(0, -1);
      if (inst.endsWith('"')) inst = inst.slice(0, -1);
      return { parsedCriteria: c, parsedInstructions: inst };
    }
    return { parsedCriteria: combinedString, parsedInstructions: null };
  };

  // Handlers
  const handleCreateSurvey = async () => {
    if (!newSurveyTitle.trim()) { toast.warning('Please enter a survey title'); return; }
    setIsCreating(true);
    try { await createSurveyMutation.mutateAsync({ title: newSurveyTitle.trim(), description: newSurveyDescription.trim() || undefined }); }
    finally { setIsCreating(false); }
  };

  const openManageDialog = (survey: Survey) => {
    setEditingSurvey(survey); setEditSurveyTitle(survey.title); setEditSurveyDescription(survey.description || ''); setManageTab('details'); setShowManageDialog(true);
  };

  const handleUpdateSurvey = async () => {
    if (!editSurveyTitle.trim() || !editingSurvey) return;
    setIsUpdating(true);
    try { await updateSurveyMutation.mutateAsync({ id: editingSurvey.id, data: { title: editSurveyTitle.trim(), description: editSurveyDescription.trim() || undefined } }); }
    finally { setIsUpdating(false); }
  };

  // Question management for create dialog
  const openQuestionDialog = (question?: Partial<Question>) => {
    if (question) { setEditingQuestion(question); setQuestionText(question.text || ''); setQuestionInstructions(question.instructions || ''); setAnswerCriteria(question.answer_criteria || ''); }
    else { setEditingQuestion(null); setQuestionText(''); setQuestionInstructions(''); setAnswerCriteria(''); }
    setShowQuestionDialog(true);
  };

  const handleSaveQuestion = () => {
    if (!questionText.trim()) { toast.warning('Please enter question text'); return; }
    const qd: Partial<Question> = { type: 'text', text: questionText.trim(), answer_criteria: answerCriteria, instructions: questionInstructions.trim() || undefined, is_required: true };
    if (editingQuestion) {
      setQuestions(questions.map(q => q === editingQuestion ? { ...qd, order: q.order } : q));
    } else {
      setQuestions([...questions, { ...qd, order: questions.length + 1 }]);
    }
    setShowQuestionDialog(false);
  };

  const handleReorderQuestion = (question: Partial<Question>, direction: 'up' | 'down') => {
    const idx = questions.indexOf(question);
    if ((direction === 'up' && idx > 0) || (direction === 'down' && idx < questions.length - 1)) {
      const newQ = [...questions]; const ni = direction === 'up' ? idx - 1 : idx + 1;
      [newQ[idx], newQ[ni]] = [newQ[ni], newQ[idx]];
      newQ.forEach((q, i) => { q.order = i + 1; });
      setQuestions(newQ);
    }
  };

  // Question management for manage dialog
  const openQuestionDialogInSurvey = (question?: Partial<Question>) => {
    if (question) { setEditingQuestionInSurvey(question); setQuestionTextInSurvey(question.text || ''); setQuestionInstructionsInSurvey(question.instructions || ''); setAnswerCriteriaInSurvey(question.answer_criteria || ''); }
    else { resetQuestionFormInSurvey(); }
    setShowQuestionDialogInSurvey(true);
  };

  const handleSaveQuestionInSurvey = async () => {
    if (!questionTextInSurvey.trim() || !editingSurvey) return;
    setIsCreatingQuestionInSurvey(true);
    try {
      if (editingQuestionInSurvey?.id) {
        await updateQuestionInSurveyMutation.mutateAsync({ id: editingQuestionInSurvey.id, data: { type: 'text', text: questionTextInSurvey.trim(), answer_criteria: answerCriteriaInSurvey, instructions: questionInstructionsInSurvey.trim() || undefined, is_required: true } });
      } else {
        await createQuestionInSurveyMutation.mutateAsync({ survey_id: editingSurvey.id, type: 'text', text: questionTextInSurvey.trim(), answer_criteria: answerCriteriaInSurvey, instructions: questionInstructionsInSurvey.trim() || undefined, is_required: true });
      }
    } finally { setIsCreatingQuestionInSurvey(false); }
  };

  const handleReorderQuestionInDialog = async (question: Question, direction: 'up' | 'down') => {
    const currentQuestions = manageSurveyQuestions || [];
    const currentIndex = currentQuestions.findIndex(q => q.id === question.id);
    if (currentIndex === -1) return;
    if ((direction === 'up' && currentIndex > 0) || (direction === 'down' && currentIndex < currentQuestions.length - 1)) {
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (!question.id || !currentQuestions[newIndex]?.id) return;
      setIsReordering(true);
      try {
        await reorderQuestionsMutation.mutateAsync([
          { question_id: question.id, new_order: newIndex + 1 },
          { question_id: currentQuestions[newIndex].id, new_order: currentIndex + 1 },
        ]);
      } finally { setIsReordering(false); }
    }
  };

  // Assign
  const handleAssignUsers = (survey: Survey) => { setSelectedSurveyForAssignment(survey); setShowAssignDialog(true); refetchAvailableUsers(); };

  const handleCreateAssignments = async () => {
    if (!selectedSurveyForAssignment || selectedUsersForAssignment.length === 0) { toast.warning('Please select at least one user'); return; }
    setIsAssigning(true);
    try { await createBulkAssignmentMutation.mutateAsync({ user_ids: selectedUsersForAssignment, survey_id: selectedSurveyForAssignment.id }); }
    finally { setIsAssigning(false); }
  };

  // Confirm actions
  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, data } = confirmAction;
    if (type === 'delete-survey' && editingSurvey) await deleteSurveyMutation.mutateAsync(editingSurvey.id);
    else if (type === 'archive-survey' && editingSurvey) await archiveSurveyMutation.mutateAsync(editingSurvey.id);
    else if (type === 'publish-survey' && editingSurvey) await publishSurveyMutation.mutateAsync(editingSurvey.id);
    else if (type === 'delete-question' && data) await deleteQuestionInSurveyMutation.mutateAsync(data.id);
    setConfirmAction(null);
  };

  const filteredSurveys = surveys?.filter(sv =>
    sv.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sv.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  const totalSurveys = filteredSurveys.length;

  // If the current page falls off the end (e.g. after deletes), step back to the new last page.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalSurveys / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [totalSurveys, pageSize, page]);

  const pagedSurveys = filteredSurveys.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <PageHeader
        title="Survey Management"
        subtitle="Create and manage surveys"
        actions={
          <Dialog open={showCreateDialog} onOpenChange={(_, data) => setShowCreateDialog(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary" icon={<Add24Regular />}>Create Survey</Button>
            </DialogTrigger>
            <DialogSurface style={{ maxWidth: '800px', width: '90vw' }}>
              <DialogBody>
                <DialogTitle>Create New Survey</DialogTitle>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                      <Field label="Survey Title" required><Input value={newSurveyTitle} onChange={(e) => setNewSurveyTitle(e.target.value)} placeholder="Enter survey title" /></Field>
                      <Field label="Description"><Textarea value={newSurveyDescription} onChange={(e) => setNewSurveyDescription(e.target.value)} placeholder="Enter survey description" rows={3} /></Field>
                    </div>
                    <Divider />
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
                        <Text weight="semibold" size={400}>Questions ({questions.length})</Text>
                        <Button appearance="primary" size="small" icon={<Add24Regular />} onClick={() => openQuestionDialog()}>Add Question</Button>
                      </div>
                      {questions.length === 0 ? (
                        <EmptyState title="No questions added" description="Click 'Add Question' to get started" />
                      ) : (
                        <div className={s.questionsArea}>
                          {questions.map((q, i) => {
                            const { parsedCriteria, parsedInstructions } = parseCriteriaAndInstructions(q.answer_criteria || null);
                            return (
                              <QuestionCard key={i} index={i} total={questions.length} text={q.text || ''} type={q.type || 'text'} isRequired={q.is_required}
                                instructions={q.instructions || parsedInstructions} criteria={parsedCriteria}
                                onEdit={() => openQuestionDialog(q)} onDelete={() => setQuestions(questions.filter(x => x !== q))}
                                onMoveUp={() => handleReorderQuestion(q, 'up')} onMoveDown={() => handleReorderQuestion(q, 'down')} />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
                  <Button appearance="primary" onClick={handleCreateSurvey} disabled={isCreating || !newSurveyTitle.trim()}>{isCreating ? 'Creating...' : 'Create Survey'}</Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        }
      />

      {/* Question Dialog (for create) */}
      <Dialog open={showQuestionDialog} onOpenChange={(_, data) => setShowQuestionDialog(data.open)}>
        <DialogSurface style={{ maxWidth: '600px', width: '90vw' }}>
          <DialogBody>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <Field label="Question Text" required><Textarea value={questionText} onChange={(e) => setQuestionText(e.target.value)} placeholder="Enter your question..." rows={3} /></Field>
                <Field label="Instructions (Optional)"><Textarea value={questionInstructions} onChange={(e) => setQuestionInstructions(e.target.value)} placeholder="Additional instructions..." rows={2} /></Field>
                <Field label="Answer Criteria (Optional)"><Textarea value={answerCriteria} onChange={(e) => setAnswerCriteria(e.target.value)} placeholder="Criteria for answers..." rows={2} /></Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
              <Button appearance="primary" onClick={handleSaveQuestion} disabled={!questionText.trim()}>{editingQuestion ? 'Update' : 'Add'}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Question Dialog (for manage) */}
      <Dialog open={showQuestionDialogInSurvey} onOpenChange={(_, data) => setShowQuestionDialogInSurvey(data.open)}>
        <DialogSurface style={{ maxWidth: '600px', width: '90vw' }}>
          <DialogBody>
            <DialogTitle>{editingQuestionInSurvey?.id ? 'Edit Question' : 'Add Question'}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <Field label="Question Text" required><Textarea value={questionTextInSurvey} onChange={(e) => setQuestionTextInSurvey(e.target.value)} placeholder="Enter your question..." rows={3} /></Field>
                <Field label="Instructions (Optional)"><Textarea value={questionInstructionsInSurvey} onChange={(e) => setQuestionInstructionsInSurvey(e.target.value)} placeholder="Additional instructions..." rows={2} /></Field>
                <Field label="Answer Criteria (Optional)"><Textarea value={answerCriteriaInSurvey} onChange={(e) => setAnswerCriteriaInSurvey(e.target.value)} placeholder="Criteria for answers..." rows={2} /></Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
              <Button appearance="primary" onClick={handleSaveQuestionInSurvey} disabled={!questionTextInSurvey.trim() || isCreatingQuestionInSurvey}>
                {isCreatingQuestionInSurvey ? 'Saving...' : (editingQuestionInSurvey?.id ? 'Update' : 'Add')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Survey list */}
      <div className={s.card}>
        <div className={s.toolbar}>
          <div className={s.searchWrap}>
            <Search24Regular className={s.searchIcon} />
            <Input placeholder="Search surveys..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '36px', width: '100%' }} />
          </div>
          <div className={s.counters}>
            <div className={s.counter}><span className={s.counterValue} style={{ color: 'var(--undp-primary)' }}>{surveys?.length || 0}</span><span className={s.counterLabel}>Total</span></div>
            <div className={s.counter}><span className={s.counterValue} style={{ color: 'var(--color-success)' }}>{surveys?.filter(sv => sv.status === 'active').length || 0}</span><span className={s.counterLabel}>Active</span></div>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}><Spinner size="large" /></div>
        ) : filteredSurveys.length === 0 ? (
          <EmptyState icon={<DocumentText24Regular />} title={searchTerm ? 'No surveys found' : 'No surveys yet'} description={searchTerm ? 'Try a different search term' : 'Create your first survey to get started'} />
        ) : (
          <>
          <div className="table-scroll">
          <Table className={s.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '35%' }}>Survey</TableHeaderCell>
                <TableHeaderCell style={{ width: '12%' }}>Status</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '12%' }}>Questions</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '15%' }}>Created</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '26%' }}>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedSurveys.map((survey) => (
                <TableRow key={survey.id}>
                  <TableCell
                    className={s.nameCell}
                    onClick={isMobile ? () => setMobileSurvey(survey) : undefined}
                  >
                    <div>
                      <Text weight="medium" size={300}>{survey.title || 'Untitled Survey'}</Text>
                      <span className={s.expandHint}>›</span>
                    </div>
                    {survey.description && <Text size={200} style={{ color: 'var(--color-text-secondary)', display: 'block' }}>{survey.description}</Text>}
                  </TableCell>
                  <TableCell><StatusBadge status={survey.status} /></TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300}>{survey.questions?.length || 0}</Text></TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300}>{survey.created_at ? new Date(survey.created_at).toLocaleDateString() : 'N/A'}</Text></TableCell>
                  <TableCell className={s.hideOnMobile}>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
                      <Button appearance="subtle" size="small" onClick={() => openManageDialog(survey)}>Manage</Button>
                      <Button appearance="subtle" size="small" onClick={() => { setSelectedSurveyForContext(survey); setShowContextDialog(true); }}>Context</Button>
                      {(survey.status === 'draft' || survey.status === 'active') && (
                        <Button appearance="subtle" size="small" icon={<People24Regular />} onClick={() => handleAssignUsers(survey)}>Assign</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination total={filteredSurveys.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }} />
          </>
        )}
      </div>

      {/* Mobile-only survey actions popup */}
      {mobileSurvey && (
        <div className={s.modalOverlay} onClick={() => setMobileSurvey(null)}>
          <div className={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <Text weight="semibold" size={400}>{mobileSurvey.title || 'Untitled Survey'}</Text>
              <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} aria-label="Close" onClick={() => setMobileSurvey(null)} />
            </div>
            <div><StatusBadge status={mobileSurvey.status} /></div>
            <div className={s.detailMeta}>
              <span>Questions: {mobileSurvey.questions?.length || 0}</span>
              <span>Created: {mobileSurvey.created_at ? new Date(mobileSurvey.created_at).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className={s.modalActions}>
              <Button appearance="primary" onClick={() => { const sv = mobileSurvey; setMobileSurvey(null); openManageDialog(sv); }}>Manage</Button>
              <Button appearance="secondary" onClick={() => { const sv = mobileSurvey; setMobileSurvey(null); setSelectedSurveyForContext(sv); setShowContextDialog(true); }}>Context</Button>
              {(mobileSurvey.status === 'draft' || mobileSurvey.status === 'active') && (
                <Button appearance="secondary" icon={<People24Regular />} onClick={() => { const sv = mobileSurvey; setMobileSurvey(null); handleAssignUsers(sv); }}>Assign</Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manage Survey Dialog with Tabs */}
      <Dialog open={showManageDialog} onOpenChange={(_, data) => setShowManageDialog(data.open)}>
        <DialogSurface style={{ maxWidth: '800px', width: '90vw' }}>
          <DialogBody>
            <DialogTitle>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Manage Survey</span>
                {editingSurvey && <StatusBadge status={editingSurvey.status} />}
              </div>
            </DialogTitle>
            <DialogContent>
              <TabList selectedValue={manageTab} onTabSelect={(_, data) => setManageTab(data.value as string)}>
                <Tab value="details">Details</Tab>
                <Tab value="questions">Questions {manageSurveyQuestions ? `(${manageSurveyQuestions.length})` : ''}</Tab>
              </TabList>

              <div className={s.tabContent}>
                {manageTab === 'details' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                    <Field label="Survey Title" required><Input value={editSurveyTitle} onChange={(e) => setEditSurveyTitle(e.target.value)} /></Field>
                    <Field label="Description"><Textarea value={editSurveyDescription} onChange={(e) => setEditSurveyDescription(e.target.value)} rows={3} /></Field>
                    {editingSurvey && (
                      <div className={s.infoBlock}>
                        <div className={s.infoRow}>ID: {editingSurvey.id}</div>
                        <div className={s.infoRow}>Created: {editingSurvey.created_at ? new Date(editingSurvey.created_at).toLocaleDateString() : 'N/A'}</div>
                        <div className={s.infoRow}>Questions: {manageSurveyQuestions?.length || 0}</div>
                      </div>
                    )}
                  </div>
                )}

                {manageTab === 'questions' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-md)' }}>
                      <Button appearance="primary" size="small" icon={<Add24Regular />} onClick={() => openQuestionDialogInSurvey()} disabled={isReordering}>Add Question</Button>
                    </div>
                    {isLoadingQuestions ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-lg)' }}><Spinner size="small" /></div>
                    ) : (manageSurveyQuestions || []).length === 0 ? (
                      <EmptyState title="No questions" description="Add questions to this survey" />
                    ) : (
                      <div className={s.questionsArea}>
                        {(manageSurveyQuestions || []).map((question, index) => {
                          const { parsedCriteria, parsedInstructions } = parseCriteriaAndInstructions(question.answer_criteria || null);
                          return (
                            <QuestionCard key={question.id} index={index} total={(manageSurveyQuestions || []).length}
                              text={question.text} type={question.type} isRequired={question.is_required}
                              instructions={question.instructions || parsedInstructions} criteria={parsedCriteria}
                              onEdit={() => openQuestionDialogInSurvey(question)}
                              onDelete={() => setConfirmAction({ type: 'delete-question', data: question })}
                              onMoveUp={() => handleReorderQuestionInDialog(question, 'up')}
                              onMoveDown={() => handleReorderQuestionInDialog(question, 'down')}
                              disabled={isReordering} />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <div className={s.manageActions}>
                <div className={s.manageActionsGroup}>
                  {editingSurvey?.status === 'draft' && <Button appearance="primary" onClick={() => setConfirmAction({ type: 'publish-survey' })} disabled={isUpdating}>Publish</Button>}
                  {editingSurvey && editingSurvey.status !== 'archived' && <Button appearance="secondary" onClick={() => setConfirmAction({ type: 'archive-survey' })} disabled={isUpdating}>Archive</Button>}
                  {editingSurvey && <Button appearance="outline" onClick={() => setConfirmAction({ type: 'delete-survey' })} disabled={isUpdating} style={{ color: 'var(--color-error)', borderColor: 'var(--color-error)' }}>Delete</Button>}
                </div>
                <div className={s.manageActionsGroup}>
                  <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
                  {manageTab === 'details' && <Button appearance="primary" onClick={handleUpdateSurvey} disabled={isUpdating || !editSurveyTitle.trim()}>{isUpdating ? 'Updating...' : 'Update'}</Button>}
                </div>
              </div>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(_, data) => setShowAssignDialog(data.open)}>
        <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>Assign Users to Survey</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                {selectedSurveyForAssignment && (
                  <div className={s.infoBlock}><Text size={300} weight="semibold">Survey: </Text><Text size={300}>{selectedSurveyForAssignment.title}</Text></div>
                )}
                <Field label="Select Users" required>
                  <div className={s.checkboxList}>
                    {availableUsers?.map((user) => (
                      <div key={user.id} className={s.checkboxItem}>
                        <Checkbox checked={selectedUsersForAssignment.includes(user.id)} onChange={(_, data) => { if (data.checked) setSelectedUsersForAssignment([...selectedUsersForAssignment, user.id]); else setSelectedUsersForAssignment(selectedUsersForAssignment.filter(id => id !== user.id)); }} />
                        <Text size={300}>{user.first_name} {user.last_name} ({user.email})</Text>
                      </div>
                    ))}
                    {availableUsers && availableUsers.length === 0 && <Text size={200} style={{ color: 'var(--color-error)', padding: 'var(--space-md)', textAlign: 'center', display: 'block' }}>No users available.</Text>}
                  </div>
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
              <Button appearance="primary" onClick={handleCreateAssignments} disabled={isAssigning || selectedUsersForAssignment.length === 0}>{isAssigning ? 'Assigning...' : `Assign ${selectedUsersForAssignment.length} Users`}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Context Editor */}
      <ContextEditor open={showContextDialog} onClose={() => { setShowContextDialog(false); setSelectedSurveyForContext(null); }} survey={selectedSurveyForContext} />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={executeConfirmAction}
        title={confirmAction?.type === 'delete-survey' ? 'Delete Survey' : confirmAction?.type === 'archive-survey' ? 'Archive Survey' : confirmAction?.type === 'publish-survey' ? 'Publish Survey' : 'Delete Question'}
        message={
          confirmAction?.type === 'delete-survey' ? `Are you sure you want to delete "${editingSurvey?.title}"? This cannot be undone.`
          : confirmAction?.type === 'archive-survey' ? `Archive "${editingSurvey?.title}"?`
          : confirmAction?.type === 'publish-survey' ? `Publish "${editingSurvey?.title}"?`
          : 'Are you sure you want to delete this question?'
        }
        confirmLabel={confirmAction?.type?.includes('delete') ? 'Delete' : confirmAction?.type === 'archive-survey' ? 'Archive' : 'Publish'}
        danger={confirmAction?.type?.includes('delete') || false}
      />
    </div>
  );
}
